
import { getFavorites } from "@/api/va-order-page/orderTest.js";
import { getShortcutsApi } from "@/api/va-order-page/pricing/item/catalog.js";
import { modifyShortcut } from "./helpers/favorites";
import { getBestDealSuggestion } from "@/utils/va-order-test/best-deal-extract/index.js";
import { cloneOrderTestTreeAndBundles } from "./helpers/deepCopyHelpers";

const favorites = {
  namespaced: true,
  state: {
    favoriteTestIdList: [],
    shortcutList: [],
    selectedShortcutIdList: [],
    shortcutPanelLoading: false,
  },
  getters: {
    selectedShortcutList(state) {
      return state.shortcutList.filter(shortcut => state.selectedShortcutIdList.includes(shortcut.id));
    },
    favTestList(state, getters, rootState, rootGetters) {
      return rootGetters["orderTest/tests/testList"]
        .filter(test => state.favoriteTestIdList.includes(test.id));
    },
    // get all items(tests, bundles, xChooseYs) included in selected shortcuts
    itemsInSelectedShortcuts(state, getters, rootState, rootGetters) {
      let shortcutTests = [];
      let shortcutDiscountPanels = [];
      let shortcutCustomPanels = [];
      let shortcutXChooseYPanels = [];
      getters.selectedShortcutList.forEach(shortcut => {
        let { testIdList, discountPanelIdList, customPanelIdList, selectedXChooseYPanelList, xChooseYIdList } = shortcut;
        let tests = rootGetters["orderTest/tests/testList"].filter(test => testIdList.includes(test.id));
        shortcutTests = shortcutTests.concat(tests);
        let discountPanels = rootGetters["orderTest/panels/bundleList"].filter(bundle => discountPanelIdList.includes(bundle.bundleId));
        shortcutDiscountPanels = shortcutDiscountPanels.concat(discountPanels);
        let customPanels = rootGetters["orderTest/panels/bundleList"].filter(bundle => customPanelIdList.includes(bundle.bundleId));
        shortcutCustomPanels = shortcutCustomPanels.concat(customPanels);
        const xChooseYList = selectedXChooseYPanelList || xChooseYIdList || [];
        if (xChooseYList.length > 0) {
          const bundles = rootGetters["orderTest/panels/bundleList"];
          let xChooseYs = xChooseYList
            .filter(xChooseY => !xChooseY.customPanelId)
            .map(xChooseY => {
              if (!xChooseY.bundleId) {
                return xChooseY;
              }
              const foundBundle = bundles.find(bundle => bundle.bundleId === xChooseY.bundleId);
              if (!foundBundle) {
                return null;
              }
              return {
                ...foundBundle,
                selectedZoomerIdList: xChooseY.selectedZoomerIdList || foundBundle.selectedZoomerIdList || [],
                selectedSupplementIdList: xChooseY.selectedSupplementIdList || foundBundle.selectedSupplementIdList || [],
              };
            })
            .filter(Boolean);
          shortcutXChooseYPanels = shortcutXChooseYPanels.concat(xChooseYs);
        }
      });
      let selectedShortcutItemList = shortcutTests.concat(shortcutDiscountPanels).concat(shortcutCustomPanels).concat(shortcutXChooseYPanels);
      return selectedShortcutItemList;
    }
  },
  mutations: {
    SET_FAVORITE_TEST_ID_LIST(state, favoriteTestIdList) {
      state.favoriteTestIdList = favoriteTestIdList;
    },
    ADD_FAVORITE_TEST_ID(state, testId) {
      state.favoriteTestIdList.push(testId);
    },
    ADD_SHORTCUT(state, shortcut) {
      state.shortcutList.push(shortcut);
    },
    UPDATE_SHORTCUT(state, { originalShortcut, newShortcut }) {
      let index = state.shortcutList.indexOf(originalShortcut);
      state.shortcutList.splice(index, 1, newShortcut);
    },
    REMOVE_SHORTCUT(state, shortcut) {
      let index = state.shortcutList.indexOf(shortcut);
      if (index > -1)
        state.shortcutList.splice(index, 1);
    },
    SET_SHORTCUT_LIST(state, modifiedShortcutList) {
      state.shortcutList = modifiedShortcutList;
    },
    REMOVE_SELECTED_SHORTCUT_ID(state, shortcutId) {
      let index = state.selectedShortcutIdList.indexOf(shortcutId);
      if (index > -1)
        state.selectedShortcutIdList.splice(index, 1);
    },
    REMOVE_FAV_TEST_ID(state, favTestId) {
      let index = state.favoriteTestIdList.indexOf(favTestId);
      if (index > -1) {
        state.favoriteTestIdList.splice(index, 1);
      }
    }
  },
  actions: {
    setFavoriteTestIdList({ commit }) {
      getFavorites().then(res => {
        let testIds = res.data.filter(e => e.itemType === "packagePriceId")
          .map(e => {
            return isNaN(e.itemId) ? e.itemId : Number(e.itemId)
          });
        commit("SET_FAVORITE_TEST_ID_LIST", testIds);
      });
    },
    addFavoriteShortcut({ commit }, shortcut) {
      commit("ADD_SHORTCUT", shortcut);
    },
    updateFavoriteShortcut({ commit }, { originalShortcut, newShortcut }) {
      commit("UPDATE_SHORTCUT", { originalShortcut, newShortcut });
    },
    removeFavoriteShortcut({ commit }, shortcut) {
      commit("REMOVE_SHORTCUT", shortcut);
    },
    removeFavTestId({ commit }, favTestId) {
      commit("REMOVE_FAV_TEST_ID", favTestId);
    },
    addFavoriteTestId({ commit }, testId) {
      commit("ADD_FAVORITE_TEST_ID", testId);
    },
    removeFavoriteTestId({ commit }, favoriteId) {
      commit("REMOVE_FAVORITE_TEST_ID", favoriteId);
    },
    async setShortcutList({ state, commit, rootState, rootGetters }) {
      state.shortcutPanelLoading = true;
      try {
        const userInfo = rootState.orderTest.user.userInfo;
        if (!userInfo) {
          state.shortcutPanelLoading = false;
          return;
        }
        const { isClinic, customer_id, clinic_id } = userInfo;
        const selectedProviderId = rootState.orderTest.user.customer?.id;
        const customerIdForApi = isClinic && selectedProviderId
          ? String(selectedProviderId)
          : customer_id;
        const params = {
          clinic_id,
          customer_id: customerIdForApi,
          account_type: isClinic ? "clinic" : "customer"
        };
        const { data } = await getShortcutsApi(params);
        let modifiedShortcutList = [];
        if (data) {
          for (const shortcut of data) {
            let modifiedShortcut = await modifyShortcut(shortcut);
            modifiedShortcutList.push(modifiedShortcut);
          }
        }
        commit("SET_SHORTCUT_LIST", modifiedShortcutList);
        state.shortcutPanelLoading = false;
      }
      catch (err) {
        state.shortcutPanelLoading = false;
      }
    },
    setShortcutSampleTypes({ rootGetters }, shortcut) {
      let sampleTypeSets = {
        blood: new Set(),
        nonBlood: new Set(),
        dryIce: new Set()
      };
      let { testIdList, discountPanelIdList, customPanelIdList, xChooseYIdList } = shortcut;
      let testIdsInXChooseY = xChooseYIdList.reduce((acc, xChooseY) => {
        let zoomerIdList = xChooseY.selectedZoomerIdList ? xChooseY.selectedZoomerIdList : [];
        let supplementIdList = xChooseY.selectedSupplementIdList ? xChooseY.selectedSupplementIdList : [];
        return acc.concat(zoomerIdList).concat(supplementIdList);
      }, []);
      let tests = rootGetters["orderTest/tests/testList"].filter(t => testIdList.concat(testIdsInXChooseY).includes(t.id));
      let bundleIds = discountPanelIdList.concat(customPanelIdList);
      let bundles = rootGetters["orderTest/panels/bundleList"].filter(b => bundleIds.includes(b.bundleId));
      ["blood", "nonBlood", "dryIce"].forEach(kit => {
        sampleTypeSets[kit] = bundles.concat(tests).map(t => t.sampleTypeSets[kit])
          .reduce((acc, currSet) => new Set([...acc, ...currSet]), new Set());
      });
      shortcut.sampleTypeSets = sampleTypeSets;
    },
    setShortcutPriceAndContents({ rootGetters, rootState }, { shortcutFrontend }) {
      // console.log('[setShortcutPriceAndContents] START for shortcut:', shortcutFrontend.shortcutName);
      let { customPanelIdList, testIdList, discountPanelIdList, xChooseYIdList, isDbs } = shortcutFrontend;
      // Create deep copy of supportObj to avoid affecting other calculations
      const deepCopies = cloneOrderTestTreeAndBundles();
      // console.log('[setShortcutPriceAndContents] deepCopies.bfsTestList.length:', deepCopies.bfsTestList.length);
      // console.log('[setShortcutPriceAndContents] deepCopies.bundleList.length:', deepCopies.bundleList.length);
      const originalSupportObj = rootGetters["orderTest/orderTestSupportObj"];
      // Build supportObj from deep copies, preserving structure needed by getBestDealSuggestion
      let supportObj = {
        testNodeList: deepCopies.bfsTestList, // Use bfsTestList as testNodeList
        vaRootNodeList: deepCopies.vaRootNodeList,
        vwRootNodeList: deepCopies.vwRootNodeList,
        regenereNodeList: deepCopies.regenereNodeList,
        nutriprozNodeList: deepCopies.nutriprozNodeList,
        bundleList: deepCopies.bundleList,
        seasonalPromotionList: originalSupportObj.seasonalPromotionList, // This is not cloned as it's not in deepCopies
        shortcutList: originalSupportObj.shortcutList, // This is not cloned as it's not in deepCopies
        exceptionMap: originalSupportObj.exceptionMap, // This is not cloned as it's not in deepCopies
        dbsMap: originalSupportObj.dbsMap, // This is not cloned as it's not in deepCopies
        bestDealSupportMapping: originalSupportObj.bestDealSupportMapping // This is not cloned as it's not in deepCopies
      };
      let test_id_list = testIdList;
      xChooseYIdList.forEach(xChooseY => {
        let zoomerIdList = xChooseY.selectedZoomerIdList ? xChooseY.selectedZoomerIdList : [];
        let supplementIdList = xChooseY.selectedSupplementIdList ? xChooseY.selectedSupplementIdList : [];
        test_id_list = test_id_list.concat(zoomerIdList).concat(supplementIdList);
      });
      let discount_panel_id_list = discountPanelIdList;
      let payload = {
        test_id_list: test_id_list.map(id => id.toString()),
        discount_panel_id_list: discount_panel_id_list.map(id => id.toString()),
        is_dbs: isDbs,
      };
      let { discountMap, dbsMap } = rootState.orderTest;
      // console.log('[setShortcutPriceAndContents] discountMap keys:', Object.keys(discountMap || {}).length);
      let bestDealSuggestion = getBestDealSuggestion(payload, supportObj, discountMap, dbsMap);
      // console.log('[setShortcutPriceAndContents] bestDealSuggestion.best_deal_price:', bestDealSuggestion.best_deal_price);
      let customPanels = rootGetters["orderTest/panels/bundleList"]
        .filter(b => customPanelIdList.includes(b.bundleId));
      // find total price for custom panels in shortcut
      let customPanelsPrice = customPanels.reduce((acc, bundle) => {
        return acc + bundle.price
      }, 0);
      // console.log('[setShortcutPriceAndContents] customPanelsPrice:', customPanelsPrice);
      shortcutFrontend.price = Number(bestDealSuggestion.best_deal_price) + customPanelsPrice;
      // console.log('[setShortcutPriceAndContents] FINAL shortcutFrontend.price:', shortcutFrontend.price);
      // udpate shortcut's testIdList, discountPanelIdList, xChooseYIdList with bestDeal and leftOverTestIdList
      shortcutFrontend.testIdList = bestDealSuggestion.left_over_test_id_list.map(id => id.includes("_") ? id : Number(id));
      shortcutFrontend.discountPanelIdList = [];
      shortcutFrontend.xChooseYIdList = [];
      bestDealSuggestion.suggest_bundle_list.forEach(bundle => {
        if (!bundle.zoomers) {
          shortcutFrontend.discountPanelIdList.push(Number(bundle.id));
        } else {
          let xChooseY = {
            bundleId: Number(bundle.id),
            selectedZoomerIdList: bundle.zoomers.map(zoomer => Number(zoomer)),
            selectedSupplementIdList: bundle.supplements.map(supplement => Number(supplement))
          };
          shortcutFrontend.xChooseYIdList.push(xChooseY);
        }
      })
    },
  }
};

export default favorites;